import {
  formatValue,
  formatRatio,
  ratioToPercent,
} from '../../../../catalogWidgets/shared/widgetFormat';
import { toneForToken } from '../../../../catalogWidgets/shared/widgetColorToken';

/**
 * A KPI's `data`, read as the v2 contract publishes it (CT-5641).
 *
 * ## What this used to be
 *
 * Five keys — `value`, `label`, `feedbackText`, `progress`, `trend` — plus a
 * chain of `??` fallbacks (`metric`, `count`, `complementaryText`,
 * `progressValue`) for the several shapes callers had invented over time. Any
 * caller with a number and a string could feed it, which was the point while
 * nobody agreed on a shape.
 *
 * A KPI now returns thirteen keys and the agreement is written down. Of those
 * thirteen the widget could read three, and one of them — `trend` — in the
 * wrong case: v2 says `UP`, the component's icons are keyed `up`. The
 * remaining eight had nowhere to go, so a card configured with a target, a
 * threshold and two comparisons drew a bare number.
 *
 * ## What it does now
 *
 * It reads the contract and nothing else, and it **formats** rather than
 * computes. Every number arrives raw with a `format` object beside it, so
 * `120000` and `{style: CURRENCY}` become `$120,000.00` here — never on the
 * backend, which would make the value impossible to align or compare, and
 * never in the markup, which is how three widgets ended up printing the same
 * number three ways.
 *
 * The one thing it still derives is the bar's length, and only because a bar
 * is drawn in percent while the contract speaks in ratios: `target_pct: 0.84`
 * is `84` units of width. The colour it is drawn in is **not** derived — the
 * backend evaluated the author's thresholds against whichever number
 * `thresholds_applies_to` named, and `color_token` is that answer.
 */

/** v2 says `UP`/`DOWN`/`FLAT`; the icon and bar palettes are keyed in lower case. */
const TREND_BY_TOKEN = Object.freeze({ UP: 'up', DOWN: 'down', FLAT: 'neutral' });

/**
 * `trend` is a **direction**, not a verdict.
 *
 * The contract reports which way the number moved and leaves what that means
 * to `display.positive_direction`. For sales, up is good; for cost, waste or
 * time-to-resolve, up is the thing you are trying to avoid — and a spend KPI
 * that paints +11.9% green is telling the reader the opposite of the truth.
 *
 * So the direction is flipped before it reaches the palette when the author
 * said `DOWN` is the good way. The arrow still points the way the number
 * went; only its colour changes, because the arrow is the fact and the colour
 * is the reading.
 */
const normaliseTrend = (trend, positiveDirection = 'UP') => {
  if (!trend) return null;
  const upper = String(trend).toUpperCase();
  const key = TREND_BY_TOKEN[upper] ?? (TREND_BY_TOKEN[trend] ? trend : null);
  if (!key || positiveDirection !== 'DOWN') return key;
  if (key === 'up') return 'down';
  if (key === 'down') return 'up';
  return key;
};

/** What a comparison's figure is *to* the headline, per `compare_as`. */
const PREPOSITION = Object.freeze({ DELTA: 'vs', SHARE: 'of', RATIO: 'of' });

/**
 * One comparison, as a sentence.
 *
 * `compare_as` decides which number the row is about — a `DELTA` reads its
 * variation, a `SHARE` its weight, a `RATIO` its multiple — so the row shows
 * the one the author asked for rather than all three.
 *
 * It is a phrase and not three columns because the three readings are not
 * interchangeable: `+20.0% vs Previous month` and `12.6% of Year to date`
 * are different claims, and in a column of right-aligned percentages they
 * look like the same one. The preposition is the smallest thing that keeps
 * them apart, and it puts the figure first, where the eye lands.
 *
 * The comparison's own `period.label` rides along in the parenthesis. For a
 * `SHARE` it is not decoration: "12.6% of the year to date" is ambiguous
 * until the reader knows how far the year has run.
 */
const toComparisonRow = (comparison, format, positiveDirection) => {
  const mode = comparison.compare_as ?? 'DELTA';
  const figure = mode === 'SHARE'
    ? formatRatio(comparison.share, { signed: false })
    : formatRatio(comparison.delta_pct);

  const value = formatValue(comparison.value, comparison.format ?? format);
  const periodLabel = comparison.period?.label ?? '';

  return {
    key: comparison.key ?? comparison.label ?? mode,
    label: comparison.label ?? periodLabel,
    preposition: PREPOSITION[mode] ?? 'vs',
    figure,
    /** `($100,000.00 · September 2026)` — the number behind the figure. */
    context: [value, periodLabel].filter(Boolean).join(' · '),
    value,
    detail: figure,
    trend: normaliseTrend(comparison.trend, positiveDirection),
    compareAs: mode,
  };
};

/**
 * @param {object|null} rawData The type's `data`, as `output.schema.json` states it.
 * @param {object} [options]
 * @param {'UP'|'DOWN'} [options.positiveDirection='UP'] The author's
 *   `display.positive_direction`. See `normaliseTrend`.
 * @returns {object} The render model. Every string is ready to print.
 */
export const mapKpiData = (rawData, { positiveDirection = 'UP' } = {}) => {
  if (!rawData) {
    return {
      value: '', label: null, caption: null, feedbackText: null,
      progress: null, trend: null, tone: 'neutral',
      target: null, comparisons: [], period: null,
    };
  }

  // A COST_KPI states its currency once, outside `format`; folding it in here
  // means every number on the card is printed in it without the drawing
  // knowing what a currency is.
  const format = rawData.currency_code
    ? { style: 'CURRENCY', currency_code: rawData.currency_code, ...(rawData.format ?? {}) }
    : rawData.format ?? null;
  const comparisons = Array.isArray(rawData.comparisons)
    ? rawData.comparisons.map((comparison) => toComparisonRow(comparison, format, positiveDirection))
    : [];

  // The caption the backend already resolved (`Ventas de octubre 2026`) beats
  // the first comparison's own line: it is the sentence the author wrote.
  const feedbackText = rawData.caption
    ?? (comparisons[0]
      ? `${comparisons[0].figure} ${comparisons[0].preposition} ${comparisons[0].label}`.trim()
      : null);

  /*
    The same drawing serves two types, and they do not mean the same thing.

    A KPI measures against a `target_value` and reports `target_pct`; a
    COST_KPI measures against a `budget_value` and reports `budget_used_pct`.
    The arithmetic is identical — how far along is this number — but the
    reading is opposite: reaching a target is the point, and reaching a budget
    is the problem. So the bar carries which one it is, and says «Budget
    $50,000 · 88% used» rather than letting a reader assume 88% of a good
    thing.

    Which one is decided by the payload, not by a type code the widget is
    never told.
  */
  const isBudget = rawData.budget_value !== undefined || rawData.budget_used_pct !== undefined;
  const targetValue = rawData.target_value ?? rawData.budget_value;
  const targetRatio = rawData.target_pct ?? rawData.budget_used_pct;
  const target = targetValue === null || targetValue === undefined
    ? null
    : {
      value: formatValue(targetValue, format),
      pct: ratioToPercent(targetRatio),
      label: isBudget ? 'Budget' : 'Target',
      pctLabel: isBudget ? 'used' : 'of target',
    };

  return {
    value: formatValue(rawData.value, format, ''),
    // v1 callers put the target in `label`; v2 has a slot for each, so the
    // label is the measure's own name and the target stands on its own.
    label: rawData.label ?? null,
    caption: rawData.caption ?? null,
    feedbackText,
    // The bar follows the target when there is one. Without a target there is
    // no ratio to draw, and a full bar would read as "complete".
    progress: target?.pct ?? null,
    trend: normaliseTrend(rawData.trend, positiveDirection),
    tone: toneForToken(rawData.color_token),
    target,
    comparisons,
    period: rawData.period ?? null,
  };
};

export default mapKpiData;
